import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { toast } from 'react-toastify';
import LoaderButton from '../loader-button/LoaderButton';
import { fmtValue, isGlobalVendor } from './registry';
import './superAdmin.css';

// Every atom here is an existing portal pattern:
//  - SettingRow / Toggle: PlatformSettings.js (STYLEGUIDE §6 "Settings row + toggle"),
//    Toggle made clickable, visuals unchanged.
//  - Card head: STYLEGUIDE §5 card section header.
//  - Confirm: ConfirmModal.js markup (reactstrap Modal, falcon buttons, LoaderButton).
//  - Feedback: react-toastify, as the old SuperAdmin page did.

export const SettingRow = ({ label, hint, children, id, highlight }) => (
  <div
    id={id}
    className="bz-row sa-setting-row"
    style={{
      padding: '14px 0',
      borderBottom: '1px solid var(--line-soft)',
      gap: 24,
      alignItems: 'flex-start',
      background: highlight ? 'var(--bg-tan)' : undefined,
      transition: 'background .6s'
    }}
  >
    <div style={{ flex: '0 0 240px' }}>
      <div style={{ fontSize: 'var(--fs-md)', fontWeight: 500 }}>{label}</div>
      {hint && (
        <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
    <div className="bz-grow">{children}</div>
  </div>
);
SettingRow.propTypes = {
  label: PropTypes.node,
  hint: PropTypes.string,
  children: PropTypes.node,
  id: PropTypes.string,
  highlight: PropTypes.bool
};

export const Toggle = ({ on, label, ariaLabel, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={ariaLabel}
    disabled={disabled}
    className="sa-toggle"
    onClick={() => onChange(!on)}
    style={{ all: 'unset', cursor: disabled ? 'default' : 'pointer', display: 'inline-flex' }}
  >
    <div className="bz-row" style={{ gap: 10, opacity: disabled ? 0.6 : 1 }}>
      <span
        style={{
          width: 32,
          height: 18,
          borderRadius: 999,
          background: on ? 'var(--bz-orange-1)' : 'var(--line-strong)',
          position: 'relative',
          flexShrink: 0,
          transition: 'background .12s'
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: on ? 16 : 2,
            width: 14,
            height: 14,
            borderRadius: 999,
            background: 'white',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            transition: 'left .12s'
          }}
        />
      </span>
      {label && <span style={{ fontSize: 'var(--fs-md)' }}>{label}</span>}
    </div>
  </button>
);
Toggle.propTypes = {
  on: PropTypes.bool,
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool
};

export const Card = ({ title, aside, children, flush }) => (
  <div className="bz-card">
    {title && (
      <div
        className="bz-row"
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--line-soft)',
          justifyContent: 'space-between',
          gap: 12
        }}
      >
        <h3 className="bz-h3">{title}</h3>
        {aside}
      </div>
    )}
    <div className="sa-card-body" style={flush ? undefined : { padding: '0 16px' }}>
      {children}
    </div>
  </div>
);
Card.propTypes = { title: PropTypes.node, aside: PropTypes.node, children: PropTypes.node, flush: PropTypes.bool };

// ---------------------------------------------------------------- apply (confirm + write + toast)
const ApplyCtx = createContext(null);

// `request(item, next)` opens the confirm modal for high-impact items, otherwise
// applies at once. `isBusy(item)` is true while that item's write is in flight, or,
// for a dashboard section, while any dashboard section's write is (the endpoint takes
// the whole display array, so two overlapping dashboard writes would race).
export const useApply = () => useContext(ApplyCtx);

// Success toasts match the old page word for word where it had one.
const successText = (item, next) => {
  const state = next ? 'enabled' : 'disabled';
  if (item.kind === 'action') return `${item.label} started.`;
  if (item.kind === 'number') return `${item.label} updated to ${fmtValue(item, next)}.`;
  if (item.kind === 'choice') return `${item.logLabel || item.label} set to ${fmtValue(item, next)}.`;
  if (item.source === 'bypass') return `${item.vendor.name} status.io bypass ${state}.`;
  if (item.source === 'vendor') return `${item.vendor.name} ${state} successfully. Navbar will refresh automatically.`;
  if (item.source === 'dashboard') return `${item.label} updated successfully.`;
  return `${item.label} ${state} successfully.`;
};

const confirmTitle = ({ item, next }) => {
  if (item.kind === 'number') return `Change ${item.label}`;
  if (item.kind === 'choice') return `Change ${item.logLabel || item.label}`;
  return `${next ? 'Enable' : 'Disable'} ${item.logLabel || item.label}`;
};

const confirmLabel = ({ item, next }) =>
  item.kind === 'number' || item.kind === 'choice' ? 'Save' : next ? 'Enable' : 'Disable';

export const ApplyProvider = ({ apply, children }) => {
  const [busy, setBusy] = useState([]); // [{ uid, source }] of writes in flight
  const [pending, setPending] = useState(null); // { item, next, message } for the confirm modal
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const isBusy = item =>
    busy.some(b => b.uid === item.uid || (item.source === 'dashboard' && b.source === 'dashboard'));

  const run = async (item, next) => {
    const mark = { uid: item.uid, source: item.source };
    setBusy(b => [...b, mark]);
    let ok = false;
    try {
      ok = await apply(item, next);
    } catch (e) {
      ok = false;
    } finally {
      setBusy(b => {
        const at = b.indexOf(mark);
        return at < 0 ? b : [...b.slice(0, at), ...b.slice(at + 1)];
      });
    }
    if (ok) toast.success(successText(item, next));
    else if (item.kind === 'action') toast.error(`Couldn't start ${item.label}`);
    else toast.error(`Failed to update ${item.logLabel || item.label}`);
  };

  const request = (item, next) => {
    if (item.kind === 'readonly') return; // no write path for a row this page cannot edit
    const message = item.confirm ? item.confirm(next) : null;
    if (message) {
      setPending({ item, next, message });
      setOpen(true);
    } else {
      run(item, next);
    }
  };

  const close = () => {
    if (!confirming) setOpen(false);
  };
  const confirm = async () => {
    setConfirming(true);
    await run(pending.item, pending.next);
    setConfirming(false);
    setOpen(false);
  };

  return (
    <ApplyCtx.Provider value={{ request, isBusy }}>
      {children}
      <Modal isOpen={open} toggle={close}>
        <ModalHeader>{pending && confirmTitle(pending)}</ModalHeader>
        <ModalBody>{pending?.message}</ModalBody>
        <ModalFooter>
          <Button color="falcon-danger" disabled={confirming} onClick={close}>
            Cancel
          </Button>
          <LoaderButton color="falcon-primary" isLoading={confirming} onClick={confirm}>
            {pending && confirmLabel(pending)}
          </LoaderButton>
        </ModalFooter>
      </Modal>
    </ApplyCtx.Provider>
  );
};
ApplyProvider.propTypes = { apply: PropTypes.func.isRequired, children: PropTypes.node };

// Number settings: bz-input + Cancel / Save, like PlatformSettings' forms.
export const NumberSetting = ({ item }) => {
  const { request, isBusy } = useApply();
  const shown = String(item.value ?? '');
  const busy = isBusy(item);
  // The text typed and not yet saved, or null. While a save is in flight the stored value
  // moves to the new number at once and, if the write fails, back again; the draft ignores
  // both, so a failed save leaves what was typed (and Save) in place for another try.
  const [edited, setEdited] = useState(null);
  useEffect(() => {
    if (!busy) setEdited(e => (e === shown ? null : e)); // the stored value caught up
  }, [shown, busy]);
  const draft = edited ?? shown;
  const n = Number(draft);
  const valid = draft !== '' && Number.isInteger(n) && n >= (item.min ?? 0) && n <= (item.max ?? Infinity);
  const dirty = draft !== shown;
  return (
    <div className="bz-row" style={{ gap: 8 }}>
      <input
        className="bz-input"
        value={draft}
        inputMode="numeric"
        aria-label={item.label}
        onChange={e => setEdited(e.target.value.replace(/[^\d]/g, ''))}
        style={{ width: 90, padding: '7px 10px', borderColor: valid ? undefined : 'var(--bad)' }}
      />
      {item.unit && <span className="bz-muted">{item.unit}</span>}
      {dirty && (
        <>
          <button type="button" className="bz-btn bz-btn--sm" onClick={() => setEdited(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="bz-btn bz-btn--sm bz-btn--accent"
            disabled={!valid || busy}
            onClick={() => request(item, n)}
          >
            Save changes
          </button>
        </>
      )}
    </div>
  );
};
NumberSetting.propTypes = { item: PropTypes.object.isRequired };

// Choice settings (the ODD source): a bz-input select, like PlatformSettings' selects.
// Picking an option applies it, through the confirm when the item asks for one.
export const ChoiceSetting = ({ item }) => {
  const { request, isBusy } = useApply();
  return (
    <select
      className="bz-input"
      value={item.value}
      aria-label={item.logLabel || item.label}
      disabled={isBusy(item)}
      onChange={e => request(item, e.target.value)}
      style={{ width: 220, padding: '7px 10px' }}
    >
      {item.choices.map(c => (
        <option key={c.value} value={c.value}>
          {c.label}
        </option>
      ))}
    </select>
  );
};
ChoiceSetting.propTypes = { item: PropTypes.object.isRequired };

// Global vendors (OpenAI) are not switched on or off from Super Admin, in any view
// (the old page never offered it). Their bypass item is a separate item and stays switchable.
export const ItemToggle = ({ item, withLabel }) => {
  const { request, isBusy } = useApply();
  if (isGlobalVendor(item)) {
    return (
      <span className="bz-muted" title="Global vendors are not switched on or off here">
        —
      </span>
    );
  }
  return (
    <Toggle
      on={Boolean(item.value)}
      label={withLabel ? (item.value ? 'Enabled' : 'Disabled') : undefined}
      ariaLabel={item.logLabel || item.label}
      disabled={isBusy(item)}
      onChange={next => request(item, next)}
    />
  );
};
ItemToggle.propTypes = { item: PropTypes.object.isRequired, withLabel: PropTypes.bool };

// A tenantSettings row that is neither boolean nor number: its value as text, no control,
// no write. Editing it as a switch would overwrite it with a boolean.
export const ReadOnlyValue = ({ item }) => (
  <span
    className="bz-row"
    style={{ gap: 8, flexWrap: 'wrap' }}
    title={`Stored as ${item.valueType || 'an unknown type'}; only boolean and number settings can be edited here`}
  >
    <span className="bz-mono" style={{ overflowWrap: 'anywhere' }}>
      {fmtValue(item, item.value)}
    </span>
    <span className="bz-muted" style={{ fontSize: 'var(--fs-xs)' }}>
      Read-only
    </span>
  </span>
);
ReadOnlyValue.propTypes = { item: PropTypes.object.isRequired };

// A task: Run now starts it (no confirm: every listed task is safe to re-run), and
// "Running…" shows while the page waits for the run to land. The button stays
// usable then; a second run queues behind the first.
export const TaskRun = ({ item }) => {
  const { request, isBusy } = useApply();
  return (
    <div className="bz-row" style={{ gap: 8 }}>
      <button
        type="button"
        className="bz-btn bz-btn--sm bz-btn--accent"
        disabled={isBusy(item)}
        onClick={() => request(item, true)}
      >
        Run now
      </button>
      {item.value && (
        <span className="bz-pill bz-pill--info">
          <span className="bz-dot" />
          Running…
        </span>
      )}
    </div>
  );
};
TaskRun.propTypes = { item: PropTypes.object.isRequired };

// The one place that decides which control an item gets, so every view agrees.
export const ItemControl = ({ item, withLabel }) => {
  if (item.kind === 'action') return <TaskRun item={item} />;
  if (item.kind === 'number') return <NumberSetting item={item} />;
  if (item.kind === 'choice') return <ChoiceSetting item={item} />;
  if (item.kind === 'readonly') return <ReadOnlyValue item={item} />;
  return <ItemToggle item={item} withLabel={withLabel} />;
};
ItemControl.propTypes = { item: PropTypes.object.isRequired, withLabel: PropTypes.bool };

const PRODUCT_PILL = {
  plan: 'bz-pill--plan',
  prevent: 'bz-pill--prevent',
  predict: 'bz-pill--predict',
  platform: 'bz-pill--info'
};
const PRODUCT_LABEL = { plan: 'Plan', prevent: 'Prevent', predict: 'Predict', platform: 'Platform' };

export const ItemPills = ({ item }) => (
  <>
    {item.product && item.typeLabel !== 'Product' && (
      <span className={`bz-pill ${PRODUCT_PILL[item.product]}`}>{PRODUCT_LABEL[item.product]}</span>
    )}
    {item.attention && (
      <span className="bz-pill bz-pill--warn">
        <span className="bz-dot" />
        {item.attention}
      </span>
    )}
    {!item.registered && <span className="bz-pill bz-pill--warn">Unmapped</span>}
  </>
);
ItemPills.propTypes = { item: PropTypes.object.isRequired };
